import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatAUD, formatPct } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { QAChartTooltip } from './QAChartTooltip';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { PortfolioSnapshot } from '../../../shared/api';

const QA_COLOURS = ['#B8431A', '#A07A3F', '#2F4636', '#8A6418', '#7A6E63', '#3D3530'];

export function PortfolioTab({ portfolio }: { portfolio: PortfolioSnapshot }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api('/api/integrations/market/refresh', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries(),
  });

  if (!portfolio || !portfolio.accounts || portfolio.accounts.length === 0) {
    return <QAEmpty inline title="No accounts yet." />;
  }

  return (
    <div className="mt-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-2 qa-card p-6">
          <p className="qa-eyebrow mb-3">Asset allocation</p>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={portfolio.allocation} dataKey="value" nameKey="bucket" innerRadius={60} outerRadius={100} stroke="#FBF7EF">
                  {portfolio.allocation.map((_, i: number) => (
                    <Cell key={i} fill={QA_COLOURS[i % QA_COLOURS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<QAChartTooltip valueFormat={(v, entry) => `${formatAUD(Number(v))}${entry.payload?.pct != null ? ` · ${formatPct(entry.payload.pct as number)}` : ''}`} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="qa-card p-6">
          <div className="flex items-baseline justify-between mb-3">
            <p className="qa-eyebrow">Refresh prices</p>
            <button onClick={() => refresh.mutate()} className="btn-ghost text-sm" disabled={refresh.isPending}>
              {refresh.isPending ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <p className="text-xs text-ink-mute mb-3">Mock provider. Real IRESS adapter requires credentials.</p>
          <ul className="space-y-1 text-sm">
            {portfolio.allocation.map((slice, i: number) => (
              <li key={slice.bucket} className="flex justify-between qa-mono">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-2 h-2" style={{ background: QA_COLOURS[i % QA_COLOURS.length] }} />
                  {slice.bucket}
                </span>
                <span>{formatPct(slice.pct)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {portfolio.accounts.map((a) => {
        const isOpen = open[a.id] !== false; // open by default
        return (
          <section key={a.id} className="qa-card mb-4">
            <header className="px-5 py-3 flex items-center justify-between cursor-pointer" onClick={() => setOpen(o => ({ ...o, [a.id]: !isOpen }))}>
              <div>
                <p className="qa-mono text-xs text-ink-mute uppercase tracking-widest">{a.type}</p>
                <h3 className="qa-display text-lg">{a.name}</h3>
              </div>
              <div className="text-right">
                <p className="qa-eyebrow">Account value</p>
                <p className="qa-mono text-lg">{formatAUD(a.value)}</p>
                <p className="text-xs text-ink-mute">{formatAUD(a.cashBalance)} cash</p>
              </div>
            </header>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="qa-table">
                  <thead>
                    <tr>
                      <th>Security</th><th>Class</th>
                      <th className="text-right">Units</th>
                      <th className="text-right">Price</th>
                      <th className="text-right">Value</th>
                      <th className="text-right">U/G/L</th>
                      <th className="text-right">Return</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.holdings.map((h) => (
                      <tr key={h.code}>
                        <td><span className="font-medium">{h.code}</span> <span className="text-ink-mute">{h.name}</span></td>
                        <td className="text-xs text-ink-mute">{h.assetClass}</td>
                        <td className="num">{h.units.toLocaleString('en-AU')}</td>
                        <td className="num">{formatAUD(h.currentPrice)}</td>
                        <td className="num">{formatAUD(h.currentValue)}</td>
                        <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                          {h.unrealisedGainLoss >= 0 ? '+' : ''}{formatAUD(h.unrealisedGainLoss)}
                        </td>
                        <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                          {h.unrealisedReturnPct >= 0 ? '+' : ''}{formatPct(h.unrealisedReturnPct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="qa-hairline-t font-medium">
                      <td colSpan={4}>Total holdings</td>
                      <td className="num">{formatAUD(a.holdings.reduce((s, h) => s + h.currentValue, 0))}</td>
                      <td></td><td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
